import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { notify } from '../utils/notifications';
import api, { facilityApi } from '../api/api';
import AdminUsageChart from './AdminUsageChart';
import FacilityAdminPanel from './admin/FacilityAdminPanel';
import '../styles/AdminDashboard.css';

const USER_TRACE_COLORS = [
    '#2563EB', '#DC2626', '#7C3AED', '#DB2777',
    '#B45309', '#0891B2', '#65A30D', '#475569',
];

function AdminDashboard() {
    const { isAdmin, loading } = useAuth();
    const navigate = useNavigate();
    const [activeTab, setActiveTab] = useState('users');

    const [searchParams] = useSearchParams();
    const tabFromQuery = searchParams.get('tab');
    useEffect(() => {
        if (tabFromQuery && tabFromQuery !== activeTab) {
            setActiveTab(tabFromQuery);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabFromQuery]);

    // Users tab state
    const [users, setUsers] = useState([]);
    const [usersLoading, setUsersLoading] = useState(false);

    // User detail state
    const [selectedUser, setSelectedUser] = useState(null);
    const [userDetailLoading, setUserDetailLoading] = useState(false);
    const [deletingUser, setDeletingUser] = useState(null);
    const [togglingUser, setTogglingUser] = useState(null);

    // Invites tab state
    const [invites, setInvites] = useState([]);
    const [invitesLoading, setInvitesLoading] = useState(false);
    const [inviteEmail, setInviteEmail] = useState('');
    const [creatingInvite, setCreatingInvite] = useState(false);

    // Metrics tab state
    const [metrics, setMetrics] = useState(null);
    const [metricsLoading, setMetricsLoading] = useState(false);
    // '' = All; facility id or '_unassigned' when filtered
    const [metricsFacilityFilter, setMetricsFacilityFilter] = useState('');
    const [metricsUserFilter, setMetricsUserFilter] = useState('');
    const [metricsFilterFacilities, setMetricsFilterFacilities] = useState([]);
    // Tracks the filter pair from the previous metrics-filter effect run so we
    // can distinguish "both still '' because nothing changed" (users list loaded)
    // from "both now '' because the user reset filters back to All".
    const prevMetricsFiltersRef = useRef({ facility: null, user: null });

    // Per-user chart traces: only in facility view (no user filter). Stable
    // color assignment by sorted uid; deleted users fall back to short uid.
    const metricsUserSeries = useMemo(() => {
        if (!metricsFacilityFilter || metricsUserFilter || !metrics?.daily) return [];
        const uids = new Set();
        metrics.daily.forEach((d) => {
            Object.keys(d.by_user || {}).forEach((uid) => uids.add(uid));
        });
        return [...uids].sort().map((uid, i) => {
            const u = users.find((x) => x.uid === uid);
            return {
                uid,
                name: u?.name || u?.email || `${uid.slice(0, 8)}…`,
                color: USER_TRACE_COLORS[i % USER_TRACE_COLORS.length],
            };
        });
    }, [metrics, metricsFacilityFilter, metricsUserFilter, users]);

    // Waitlist tab state
    const [waitlistEntries, setWaitlistEntries] = useState([]);
    const [waitlistStats, setWaitlistStats] = useState(null);
    const [waitlistLoading, setWaitlistLoading] = useState(false);
    const [updatingStatus, setUpdatingStatus] = useState(null);
    const [deletingEntry, setDeletingEntry] = useState(null);

    // Feedback tab state
    const [feedbackItems, setFeedbackItems] = useState([]);
    const [feedbackLoading, setFeedbackLoading] = useState(false);
    const [updatingFeedback, setUpdatingFeedback] = useState(null);

    // Facility configs (for facility type dropdown)
    const [facilityConfigs, setFacilityConfigs] = useState([]);
    const [updatingFacilityType, setUpdatingFacilityType] = useState(false);

    const fetchUsers = useCallback(async () => {
        setUsersLoading(true);
        try {
            const resp = await api.get('/admin/users');
            const sorted = resp.data.sort((a, b) => {
                const da = a.created_at ? new Date(a.created_at) : new Date(0);
                const db = b.created_at ? new Date(b.created_at) : new Date(0);
                return db - da;
            });
            setUsers(sorted);
        } catch (err) {
            notify.error('Failed to load users');
        } finally {
            setUsersLoading(false);
        }
    }, []);

    const fetchUserDetail = useCallback(async (uid) => {
        setUserDetailLoading(true);
        try {
            const resp = await api.get(`/admin/users/${uid}`);
            setSelectedUser(resp.data);
        } catch (err) {
            notify.error('Failed to load user details');
        } finally {
            setUserDetailLoading(false);
        }
    }, []);

    const fetchInvites = useCallback(async () => {
        setInvitesLoading(true);
        try {
            const resp = await api.get('/admin/invites');
            setInvites(resp.data);
        } catch (err) {
            notify.error('Failed to load invites');
        } finally {
            setInvitesLoading(false);
        }
    }, []);

    const fetchMetrics = useCallback(async (facilityFilter, userFilter, allUsers) => {
        setMetricsLoading(true);
        try {
            const params = {};
            if (userFilter) {
                // Derive facility_id: use current facilityFilter if set, else look up from the user record.
                // Note: per-user history lives under the user's *current* facility rollup;
                // users who changed facilities will only show data from their current assignment.
                const resolvedFacilityId = facilityFilter || (() => {
                    const u = (allUsers || []).find((x) => x.uid === userFilter);
                    return u?.facilityId || '_unassigned';
                })();
                params.facility_id = resolvedFacilityId;
                params.user_id = userFilter;
            } else if (facilityFilter) {
                params.facility_id = facilityFilter;
            }
            const resp = await api.get('/admin/usage', { params });
            setMetrics(resp.data);
        } catch (err) {
            notify.error('Failed to load metrics');
        } finally {
            setMetricsLoading(false);
        }
    }, []);

    const fetchWaitlist = useCallback(async () => {
        setWaitlistLoading(true);
        try {
            const [entriesResp, statsResp] = await Promise.all([
                api.get('/admin/waitlist'),
                api.get('/admin/waitlist/stats'),
            ]);
            setWaitlistEntries(entriesResp.data);
            setWaitlistStats(statsResp.data);
        } catch (err) {
            notify.error('Failed to load waitlist');
        } finally {
            setWaitlistLoading(false);
        }
    }, []);

    const fetchFeedback = useCallback(async () => {
        setFeedbackLoading(true);
        try {
            const resp = await api.get('/admin/feedback');
            setFeedbackItems(resp.data);
        } catch (err) {
            notify.error('Failed to load feedback');
        } finally {
            setFeedbackLoading(false);
        }
    }, []);

    const handleDeleteWaitlistEntry = async (entryId, name) => {
        if (!window.confirm(`Remove ${name} from the waitlist?`)) return;
        setDeletingEntry(entryId);
        try {
            await api.delete(`/admin/waitlist/${entryId}`);
            notify.success('Entry removed');
            fetchWaitlist();
        } catch (err) {
            notify.error('Failed to delete entry');
        } finally {
            setDeletingEntry(null);
        }
    };

    const handleUpdateWaitlistStatus = async (entryId, newStatus) => {
        setUpdatingStatus(entryId);
        try {
            await api.post(`/admin/waitlist/${entryId}/status`, { status: newStatus });
            notify.success(`Status updated to "${newStatus}"`);
            fetchWaitlist();
        } catch (err) {
            notify.error('Failed to update status');
        } finally {
            setUpdatingStatus(null);
        }
    };

    const handleUpdateFeedbackStatus = async (feedbackId, newStatus) => {
        setUpdatingFeedback(feedbackId);
        try {
            await api.post(`/admin/feedback/${feedbackId}/status`, { status: newStatus });
            notify.success(`Feedback marked as "${newStatus}"`);
            fetchFeedback();
        } catch (err) {
            notify.error('Failed to update feedback status');
        } finally {
            setUpdatingFeedback(null);
        }
    };

    useEffect(() => {
        if (loading) return;
        if (!isAdmin) return;

        if (activeTab === 'users') fetchUsers();
        else if (activeTab === 'invites') fetchInvites();
        else if (activeTab === 'metrics') {
            fetchMetrics(metricsFacilityFilter, metricsUserFilter, users);
            // Load user list for user filter dropdown (reuse existing users state)
            if (users.length === 0) fetchUsers();
            // Load facility list for facility filter dropdown
            if (metricsFilterFacilities.length === 0) {
                facilityApi.list()
                    .then((list) => setMetricsFilterFacilities(list))
                    .catch(() => {});
            }
        }
        else if (activeTab === 'waitlist') fetchWaitlist();
        else if (activeTab === 'feedback') fetchFeedback();
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab, isAdmin, loading]);

    // Re-fetch metrics when filters change (only when metrics tab is active).
    // Handles four transitions:
    //   initial mount / tab open  → skip (tab-switch effect already fetched)
    //   users list loads (both filters still '')  → skip (no filter change)
    //   filter set to a value     → fetch with new filter
    //   filter reset back to ''   → fetch unfiltered (prev pair was non-'')
    useEffect(() => {
        if (loading || !isAdmin || activeTab !== 'metrics') return;
        const prev = prevMetricsFiltersRef.current;
        const bothBlank = !metricsFacilityFilter && !metricsUserFilter;
        const prevBothBlank = !prev.facility && !prev.user;
        // Update ref before the early-return so next run sees current values.
        prevMetricsFiltersRef.current = { facility: metricsFacilityFilter, user: metricsUserFilter };
        // Skip only when both current and previous filters are blank — that
        // means this fired because the users list loaded, not because a filter
        // actually changed.  When the user resets from a filter back to All,
        // prevBothBlank is false so we fall through and refetch.
        if (bothBlank && prevBothBlank) return;
        fetchMetrics(metricsFacilityFilter, metricsUserFilter, users);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [metricsFacilityFilter, metricsUserFilter, users]);

    // Fetch facility configs once for the facility type dropdown
    useEffect(() => {
        if (!isAdmin) return;
        api.get('/admin/facility-configs')
            .then((resp) => setFacilityConfigs(resp.data.filter((c) => c.active !== false)))
            .catch(() => {});
    }, [isAdmin]);

    const handleFacilityTypeChange = async (uid, facilityType) => {
        setUpdatingFacilityType(true);
        try {
            await api.post(`/admin/users/${uid}/facility-type`, { facility_type: facilityType });
            setSelectedUser((prev) => prev ? { ...prev, facility_type: facilityType } : prev);
            notify.success(`Facility type updated to "${facilityType}"`);
        } catch {
            notify.error('Failed to update facility type');
        } finally {
            setUpdatingFacilityType(false);
        }
    };

    const buildInviteMessage = (code) => {
        return `You've been invited to CensusZen!\n\nYour invite code: ${code}\n\nRegister here: ${window.location.origin}/register?code=${code}`;
    };

    const handleCreateInvite = async () => {
        const email = inviteEmail.trim() || undefined;
        setCreatingInvite(true);
        try {
            const resp = await api.post('/admin/invites', { email });
            const { code } = resp.data;

            if (email) {
                const subject = encodeURIComponent("You've been invited to CensusZen");
                const body = encodeURIComponent(buildInviteMessage(code));
                const gmailUrl = `https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(email)}&su=${subject}&body=${body}`;
                window.open(gmailUrl, '_blank');
            }

            notify.success(`Invite code "${code}" created`);
            setInviteEmail('');
            fetchInvites();
        } catch (err) {
            const detail = err.response?.data?.detail || 'Failed to create invite';
            notify.error(detail);
        } finally {
            setCreatingInvite(false);
        }
    };

    const handleCopyInvite = (code, email) => {
        const text = email
            ? `To: ${email}\n\n${buildInviteMessage(code)}`
            : buildInviteMessage(code);
        navigator.clipboard.writeText(text).then(() => {
            notify.success('Invite copied to clipboard');
        });
    };

    const handleToggleDisabled = async (uid, currentDisabled) => {
        setTogglingUser(uid);
        try {
            await api.post(`/admin/users/${uid}/disable`, { disabled: !currentDisabled });
            notify.success(currentDisabled ? 'User enabled' : 'User disabled');
            await fetchUserDetail(uid);
            fetchUsers();
        } catch (err) {
            const detail = err.response?.data?.detail || 'Failed to update user';
            notify.error(detail);
        } finally {
            setTogglingUser(null);
        }
    };

    const handleDeleteUser = async (uid, name, caseCount) => {
        const confirmed = window.confirm(
            `Permanently delete ${name || 'this user'}? This will delete ${caseCount} case${caseCount !== 1 ? 's' : ''}, all uploaded files, and their account.`
        );
        if (!confirmed) return;

        setDeletingUser(uid);
        try {
            const resp = await api.delete(`/admin/users/${uid}?reset_invite=true`);
            notify.success(
                `Deleted user: ${resp.data.cases_deleted} case${resp.data.cases_deleted !== 1 ? 's' : ''}, ${resp.data.files_deleted} file${resp.data.files_deleted !== 1 ? 's' : ''} removed`
            );
            setSelectedUser(null);
            fetchUsers();
        } catch (err) {
            const detail = err.response?.data?.detail || 'Failed to delete user';
            notify.error(detail);
        } finally {
            setDeletingUser(null);
        }
    };

    if (loading) {
        return <div className="admin-dashboard"><p>Loading...</p></div>;
    }

    if (!isAdmin) {
        return (
            <div className="admin-dashboard">
                <div className="admin-access-denied">
                    <h2>Access Denied</h2>
                    <p>You do not have admin privileges.</p>
                    <button onClick={() => navigate('/cases')} className="admin-back-button">
                        Back to Cases
                    </button>
                </div>
            </div>
        );
    }

    const formatDate = (dateStr) => {
        if (!dateStr) return '—';
        return new Date(dateStr).toLocaleDateString();
    };

    const renderUserDetail = () => {
        if (userDetailLoading) {
            return <p className="admin-loading">Loading user details...</p>;
        }
        if (!selectedUser) return null;

        const u = selectedUser;
        return (
            <div className="admin-user-detail">
                <button
                    className="admin-back-link"
                    onClick={() => setSelectedUser(null)}
                >
                    &larr; Back to user list
                </button>

                <div className="admin-detail-card">
                    <div className="admin-detail-header">
                        <div>
                            <div className="admin-detail-name-row">
                                <h2>{u.name || 'Unnamed'}</h2>
                                {u.is_admin && (
                                    <span className="admin-status-pill admin">Admin</span>
                                )}
                            </div>
                            <p className="admin-item-detail">{u.email}</p>
                        </div>
                        <div className="admin-detail-actions">
                            <button
                                className="admin-warning-button"
                                onClick={() => handleToggleDisabled(u.uid, u.disabled)}
                                disabled={togglingUser === u.uid}
                            >
                                {togglingUser === u.uid
                                    ? 'Updating...'
                                    : u.disabled ? 'Enable' : 'Disable'}
                            </button>
                            <button
                                className="admin-danger-button"
                                onClick={() => handleDeleteUser(u.uid, u.name, u.cases.length)}
                                disabled={deletingUser === u.uid}
                            >
                                {deletingUser === u.uid ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>

                    <div className="admin-detail-info">
                        <div className="admin-detail-field">
                            <span className="admin-detail-label">Organization</span>
                            <span>{u.organization || '—'}</span>
                        </div>
                        <div className="admin-detail-field">
                            <span className="admin-detail-label">Joined</span>
                            <span>{formatDate(u.created_at)}</span>
                        </div>
                        <div className="admin-detail-field">
                            <span className="admin-detail-label">Invite Code</span>
                            <span className="admin-invite-code">{u.invite_code || '—'}</span>
                        </div>
                        <div className="admin-detail-field">
                            <span className="admin-detail-label">ToS Accepted</span>
                            <span>{formatDate(u.tos_accepted_at)}</span>
                        </div>
                        <div className="admin-detail-field">
                            <span className="admin-detail-label">Status</span>
                            <span className={`admin-status-pill ${u.disabled ? 'disabled' : 'active'}`}>
                                {u.disabled ? 'Disabled' : 'Active'}
                            </span>
                        </div>
                        <div className="admin-detail-field">
                            <span className="admin-detail-label">Email Verified</span>
                            <span>{u.email_verified ? 'Yes' : 'No'}</span>
                        </div>
                        <div className="admin-detail-field">
                            <span className="admin-detail-label">Facility Type</span>
                            <select
                                className="admin-facility-select"
                                value={u.facility_type || ''}
                                onChange={(e) => handleFacilityTypeChange(u.uid, e.target.value)}
                                disabled={updatingFacilityType}
                            >
                                <option value="">Not set</option>
                                {facilityConfigs.map((c) => (
                                    <option key={c.id} value={c.id}>{c.name} ({c.id})</option>
                                ))}
                            </select>
                        </div>
                    </div>

                    <div className="admin-stat-cards">
                        <div className="admin-stat-card">
                            <span className="admin-stat-label">Cases</span>
                            <span className="admin-stat-value">{u.cases.length}</span>
                        </div>
                        <div className="admin-stat-card">
                            <span className="admin-stat-label">Total Cost</span>
                            <span className="admin-stat-value">${u.total_cost_usd.toFixed(2)}</span>
                        </div>
                        <div className="admin-stat-card">
                            <span className="admin-stat-label">Prompt Tokens</span>
                            <span className="admin-stat-value">{u.total_prompt_tokens.toLocaleString()}</span>
                        </div>
                        <div className="admin-stat-card">
                            <span className="admin-stat-label">Completion Tokens</span>
                            <span className="admin-stat-value">{u.total_completion_tokens.toLocaleString()}</span>
                        </div>
                        <div className="admin-stat-card">
                            <span className="admin-stat-label">Cached Tokens</span>
                            <span className="admin-stat-value">{u.total_cached_tokens.toLocaleString()}</span>
                        </div>
                    </div>

                    {u.cases.length > 0 && (
                        <div className="admin-detail-cases">
                            <h3>Cases</h3>
                            <div className="admin-list">
                                {u.cases.map((c) => (
                                    <div key={c.case_id} className="admin-item">
                                        <div className="admin-item-main">
                                            <h3>{c.patient_name || 'Unnamed Patient'}</h3>
                                            <p className="admin-item-detail">
                                                {c.file_count} file{c.file_count !== 1 ? 's' : ''}
                                                {c.admission_status && ` \u00B7 ${c.admission_status}`}
                                            </p>
                                        </div>
                                        <div className="admin-item-meta">
                                            {c.status?.condition && (
                                                <span className={`admin-status-pill ${c.status.condition}`}>
                                                    {c.status.condition}
                                                </span>
                                            )}
                                            <span className="admin-pill">${c.estimated_cost_usd.toFixed(2)}</span>
                                            <span className="admin-item-date">{formatDate(c.last_modified)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            </div>
        );
    };

    return (
        <div className="admin-dashboard">
            <div className="admin-header">
                <h1>Platform Admin</h1>
                <div className="admin-tabs">
                    <button
                        className={`admin-tab ${activeTab === 'users' ? 'active' : ''}`}
                        onClick={() => { setActiveTab('users'); setSelectedUser(null); }}
                    >
                        Users
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'invites' ? 'active' : ''}`}
                        onClick={() => setActiveTab('invites')}
                    >
                        Invites
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'metrics' ? 'active' : ''}`}
                        onClick={() => setActiveTab('metrics')}
                    >
                        Metrics
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'waitlist' ? 'active' : ''}`}
                        onClick={() => setActiveTab('waitlist')}
                    >
                        Waitlist
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'feedback' ? 'active' : ''}`}
                        onClick={() => setActiveTab('feedback')}
                    >
                        Feedback
                    </button>
                    <button
                        className={`admin-tab ${activeTab === 'facilities' ? 'active' : ''}`}
                        onClick={() => setActiveTab('facilities')}
                    >
                        Facilities
                    </button>
                    <button
                        className="admin-tab"
                        onClick={() => navigate('/admin/workflows')}
                    >
                        Workflows &rarr;
                    </button>
                </div>
            </div>

            <div className="admin-panel">
                {activeTab === 'users' && (
                    <div className="admin-users">
                        {selectedUser || userDetailLoading ? (
                            renderUserDetail()
                        ) : usersLoading ? (
                            <p className="admin-loading">Loading users...</p>
                        ) : users.length === 0 ? (
                            <p className="admin-empty">No registered users.</p>
                        ) : (
                            <div className="admin-list">
                                {users.map((u) => (
                                    <div
                                        key={u.uid}
                                        className="admin-item clickable"
                                        onClick={() => fetchUserDetail(u.uid)}
                                    >
                                        <div className="admin-item-main">
                                            <h3>{u.name || 'Unnamed'}</h3>
                                            <p className="admin-item-detail">{u.email}</p>
                                        </div>
                                        <div className="admin-item-meta">
                                            {u.is_admin && (
                                                <span className="admin-status-pill admin">Admin</span>
                                            )}
                                            {u.disabled && (
                                                <span className="admin-status-pill disabled">Disabled</span>
                                            )}
                                            {u.organization && (
                                                <span className="admin-pill">{u.organization}</span>
                                            )}
                                            <span className="admin-pill">{u.case_count} case{u.case_count !== 1 ? 's' : ''}</span>
                                            <span className="admin-item-date">Joined {formatDate(u.created_at)}</span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'invites' && (
                    <div className="admin-invites">
                        <div className="admin-invite-form">
                            <input
                                type="email"
                                value={inviteEmail}
                                onChange={(e) => setInviteEmail(e.target.value)}
                                placeholder="Enter recipient email (optional)"
                                className="admin-invite-input"
                                onKeyDown={(e) => e.key === 'Enter' && handleCreateInvite()}
                            />
                            <button
                                onClick={handleCreateInvite}
                                disabled={creatingInvite}
                                className="admin-invite-button"
                            >
                                {creatingInvite ? 'Sending...' : 'Send Invite'}
                            </button>
                        </div>
                        {invitesLoading ? (
                            <p className="admin-loading">Loading invites...</p>
                        ) : invites.length === 0 ? (
                            <p className="admin-empty">No invite codes yet.</p>
                        ) : (
                            <div className="admin-list">
                                {invites.map((inv) => (
                                    <div key={inv.code} className="admin-item">
                                        <div className="admin-item-main">
                                            <h3 className="admin-invite-code">{inv.code}</h3>
                                            {inv.email && (
                                                <p className="admin-item-detail">{inv.email}</p>
                                            )}
                                            {inv.used && inv.used_by && (
                                                <p className="admin-item-detail">
                                                    Used by {inv.used_by}
                                                    {inv.used_at && ` on ${formatDate(inv.used_at)}`}
                                                </p>
                                            )}
                                        </div>
                                        <div className="admin-item-meta">
                                            {!inv.used && (
                                                <button
                                                    className="admin-copy-button"
                                                    onClick={() => handleCopyInvite(inv.code, inv.email)}
                                                >
                                                    Copy
                                                </button>
                                            )}
                                            <span className={`admin-status-pill ${inv.used ? 'used' : 'available'}`}>
                                                {inv.used ? 'Used' : 'Available'}
                                            </span>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                )}

                {activeTab === 'metrics' && (
                    <div className="admin-metrics">
                        {/* Filter dropdowns — facility first, then user scoped to that facility */}
                        <div className="admin-metrics-filters">
                            <select
                                className="admin-facility-select"
                                value={metricsFacilityFilter}
                                onChange={(e) => {
                                    setMetricsFacilityFilter(e.target.value);
                                    // Reset user filter when facility changes to avoid out-of-scope selection
                                    setMetricsUserFilter('');
                                }}
                            >
                                <option value="">All Facilities</option>
                                {metricsFilterFacilities.map((f) => (
                                    <option key={f.id} value={f.id}>{f.name}</option>
                                ))}
                                <option value="_unassigned">Unassigned</option>
                            </select>
                            <select
                                className="admin-facility-select"
                                value={metricsUserFilter}
                                onChange={(e) => setMetricsUserFilter(e.target.value)}
                            >
                                <option value="">All Users</option>
                                {users
                                    .filter((u) => {
                                        if (!metricsFacilityFilter) return true;
                                        if (metricsFacilityFilter === '_unassigned') return !u.facilityId;
                                        return u.facilityId === metricsFacilityFilter;
                                    })
                                    .map((u) => (
                                        <option key={u.uid} value={u.uid}>{u.email}</option>
                                    ))
                                }
                            </select>
                        </div>

                        {metricsLoading ? (
                            <p className="admin-loading">Loading metrics...</p>
                        ) : !metrics ? (
                            <p className="admin-empty">No metrics available.</p>
                        ) : (() => {
                            // When a facility or user filter is active the backend omits cost/token fields
                            const isFiltered = !!(metricsFacilityFilter || metricsUserFilter);
                            const totals = metrics.totals;
                            const avgTotalMs = totals.total_cases_processed
                                ? Math.round((totals.total_processing_time_ms || 0) / totals.total_cases_processed)
                                : 0;
                            const avgTotalSec = (avgTotalMs / 1000).toFixed(1);
                            return (
                                <>
                                    <div className="admin-stat-cards">
                                        <div className="admin-stat-card">
                                            <span className="admin-stat-label">Total Cases</span>
                                            <span className="admin-stat-value">{totals.total_cases_processed}</span>
                                        </div>
                                        {!isFiltered && totals.total_cost_usd != null && (
                                            <div className="admin-stat-card">
                                                <span className="admin-stat-label">Total Cost</span>
                                                <span className="admin-stat-value">${totals.total_cost_usd.toFixed(2)}</span>
                                            </div>
                                        )}
                                        {!isFiltered && totals.total_prompt_tokens != null && (
                                            <div className="admin-stat-card">
                                                <span className="admin-stat-label">Prompt Tokens</span>
                                                <span className="admin-stat-value">{totals.total_prompt_tokens.toLocaleString()}</span>
                                            </div>
                                        )}
                                        {!isFiltered && totals.total_completion_tokens != null && (
                                            <div className="admin-stat-card">
                                                <span className="admin-stat-label">Completion Tokens</span>
                                                <span className="admin-stat-value">{totals.total_completion_tokens.toLocaleString()}</span>
                                            </div>
                                        )}
                                        {!isFiltered && totals.total_cached_tokens != null && (
                                            <div className="admin-stat-card">
                                                <span className="admin-stat-label">Cached Tokens</span>
                                                <span className="admin-stat-value">{totals.total_cached_tokens.toLocaleString()}</span>
                                            </div>
                                        )}
                                        {isFiltered && (
                                            <div className="admin-stat-card">
                                                <span className="admin-stat-label">Avg Time</span>
                                                <span className="admin-stat-value">{avgTotalMs ? `${avgTotalSec}s` : '—'}</span>
                                            </div>
                                        )}
                                    </div>

                                    {metrics.daily.length > 0 && (
                                        <AdminUsageChart
                                            daily={metrics.daily}
                                            showCost={!isFiltered}
                                            userSeries={metricsUserSeries}
                                        />
                                    )}

                                    {metrics.daily.length > 0 && (
                                        <div className="admin-usage-table-wrapper">
                                            <h3>Daily Usage</h3>
                                            <table className="admin-usage-table">
                                                <thead>
                                                    <tr>
                                                        <th>Date</th>
                                                        <th>Cases</th>
                                                        <th>Avg Time</th>
                                                        {!isFiltered && <th>Cost</th>}
                                                        {!isFiltered && <th>Prompt</th>}
                                                        {!isFiltered && <th>Completion</th>}
                                                        {!isFiltered && <th>Cached</th>}
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {metrics.daily.map((d) => {
                                                        const avgMs = d.total_cases_processed
                                                            ? Math.round((d.total_processing_time_ms || 0) / d.total_cases_processed)
                                                            : 0;
                                                        const avgSec = (avgMs / 1000).toFixed(1);
                                                        return (
                                                        <tr key={d.date}>
                                                            <td>{d.date}</td>
                                                            <td className="numeric">{d.total_cases_processed}</td>
                                                            <td className="numeric">{avgMs ? `${avgSec}s` : '—'}</td>
                                                            {!isFiltered && <td className="numeric">${(d.total_cost_usd ?? 0).toFixed(4)}</td>}
                                                            {!isFiltered && <td className="numeric">{(d.total_prompt_tokens ?? 0).toLocaleString()}</td>}
                                                            {!isFiltered && <td className="numeric">{(d.total_completion_tokens ?? 0).toLocaleString()}</td>}
                                                            {!isFiltered && <td className="numeric">{(d.total_cached_tokens ?? 0).toLocaleString()}</td>}
                                                        </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </>
                            );
                        })()}
                    </div>
                )}

                {activeTab === 'waitlist' && (
                    <div className="admin-waitlist">
                        {waitlistLoading ? (
                            <p className="admin-loading">Loading waitlist...</p>
                        ) : (
                            <>
                                {waitlistStats && (
                                    <div className="admin-stat-cards">
                                        <div className="admin-stat-card">
                                            <span className="admin-stat-label">Total Requests</span>
                                            <span className="admin-stat-value">{waitlistStats.total}</span>
                                        </div>
                                        {Object.entries(waitlistStats.by_facility_type || {}).map(([type, count]) => (
                                            <div key={type} className="admin-stat-card">
                                                <span className="admin-stat-label">{type}</span>
                                                <span className="admin-stat-value">{count}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {waitlistEntries.length === 0 ? (
                                    <p className="admin-empty">No waitlist requests yet.</p>
                                ) : (
                                    <div className="admin-list">
                                        {waitlistEntries.map((entry) => (
                                            <div key={entry.id} className="admin-item">
                                                <div className="admin-item-main">
                                                    <h3>{entry.name}</h3>
                                                    <p className="admin-item-detail">
                                                        {entry.email} &middot; {entry.organization}
                                                    </p>
                                                </div>
                                                <div className="admin-item-meta">
                                                    <span className="admin-pill">
                                                        {entry.facility_type}
                                                        {entry.facility_type_other && `: ${entry.facility_type_other}`}
                                                    </span>
                                                    <select
                                                        className="admin-waitlist-status-select"
                                                        value={entry.status}
                                                        onChange={(e) => handleUpdateWaitlistStatus(entry.id, e.target.value)}
                                                        disabled={updatingStatus === entry.id}
                                                    >
                                                        <option value="pending">Pending</option>
                                                        <option value="contacted">Contacted</option>
                                                        <option value="invited">Invited</option>
                                                    </select>
                                                    <span className={`admin-status-pill waitlist-${entry.status}`}>
                                                        {entry.status}
                                                    </span>
                                                    <span className="admin-item-date">{formatDate(entry.created_at)}</span>
                                                    <button
                                                        className="admin-waitlist-delete-btn"
                                                        onClick={() => handleDeleteWaitlistEntry(entry.id, entry.name)}
                                                        disabled={deletingEntry === entry.id}
                                                        title="Remove entry"
                                                    >
                                                        {deletingEntry === entry.id ? '...' : '\u00D7'}
                                                    </button>
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'feedback' && (
                    <div className="admin-feedback">
                        {feedbackLoading ? (
                            <p className="admin-loading">Loading feedback...</p>
                        ) : feedbackItems.length === 0 ? (
                            <p className="admin-empty">No feedback submitted yet.</p>
                        ) : (
                            <>
                                <div className="admin-stat-cards">
                                    <div className="admin-stat-card">
                                        <span className="admin-stat-label">Total</span>
                                        <span className="admin-stat-value">{feedbackItems.length}</span>
                                    </div>
                                    <div className="admin-stat-card">
                                        <span className="admin-stat-label">New</span>
                                        <span className="admin-stat-value">
                                            {feedbackItems.filter(f => f.status === 'new').length}
                                        </span>
                                    </div>
                                    <div className="admin-stat-card">
                                        <span className="admin-stat-label">Reviewed</span>
                                        <span className="admin-stat-value">
                                            {feedbackItems.filter(f => f.status === 'reviewed').length}
                                        </span>
                                    </div>
                                    <div className="admin-stat-card">
                                        <span className="admin-stat-label">Actioned</span>
                                        <span className="admin-stat-value">
                                            {feedbackItems.filter(f => f.status === 'actioned').length}
                                        </span>
                                    </div>
                                </div>

                                <div className="admin-list">
                                    {feedbackItems.map((fb) => (
                                        <div key={fb.id} className="admin-item">
                                            <div className="admin-item-main">
                                                <h3>{fb.user_name || 'Unknown User'}</h3>
                                                <p className="admin-item-detail">{fb.comment}</p>
                                                {fb.page && (
                                                    <p className="admin-item-detail">Page: {fb.page}</p>
                                                )}
                                            </div>
                                            <div className="admin-item-meta">
                                                <span className={`admin-status-pill feedback-${fb.category}`}>
                                                    {fb.category}
                                                </span>
                                                <select
                                                    className="admin-feedback-status-select"
                                                    value={fb.status}
                                                    onChange={(e) => handleUpdateFeedbackStatus(fb.id, e.target.value)}
                                                    disabled={updatingFeedback === fb.id}
                                                >
                                                    <option value="new">New</option>
                                                    <option value="reviewed">Reviewed</option>
                                                    <option value="actioned">Actioned</option>
                                                    <option value="dismissed">Dismissed</option>
                                                </select>
                                                <span className={`admin-status-pill feedback-status-${fb.status}`}>
                                                    {fb.status}
                                                </span>
                                                <span className="admin-item-date">{formatDate(fb.created_at)}</span>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                )}

                {activeTab === 'facilities' && <FacilityAdminPanel />}
            </div>
        </div>
    );
}

export default AdminDashboard;
